import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useApprovalDelegates, useCreateDelegate } from "@/hooks/useApprovalPolicies";
import { Calendar, UserPlus, Trash2 } from "lucide-react";

export function DelegationManager({ userId }: { userId: string }) {
  const { data: delegates } = useApprovalDelegates(userId);
  const createDelegate = useCreateDelegate();
  const [showForm, setShowForm] = useState(false);
  const [newDelegate, setNewDelegate] = useState({
    delegate_id: "",
    valid_from: "",
    valid_until: "",
    delegation_reason: "",
  });

  const handleCreate = async () => {
    await createDelegate.mutateAsync({
      delegator_id: userId,
      ...newDelegate,
      is_active: true,
    });
    setShowForm(false);
    setNewDelegate({
      delegate_id: "",
      valid_from: "",
      valid_until: "",
      delegation_reason: "",
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Approval Delegation
        </CardTitle>
        <CardDescription>
          Delegate your approval authority during vacation or absence
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm ? (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
            <div className="space-y-2">
              <Label>Delegate To (User ID)</Label>
              <Input
                value={newDelegate.delegate_id}
                onChange={(e) =>
                  setNewDelegate({ ...newDelegate, delegate_id: e.target.value })
                }
                placeholder="Enter user ID"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Valid From</Label>
                <Input
                  type="date"
                  value={newDelegate.valid_from}
                  onChange={(e) =>
                    setNewDelegate({ ...newDelegate, valid_from: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Valid Until</Label>
                <Input
                  type="date"
                  value={newDelegate.valid_until}
                  onChange={(e) =>
                    setNewDelegate({ ...newDelegate, valid_until: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Input
                value={newDelegate.delegation_reason}
                onChange={(e) =>
                  setNewDelegate({ ...newDelegate, delegation_reason: e.target.value })
                }
                placeholder="e.g., Vacation"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={createDelegate.isPending}>
                Create Delegation
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button onClick={() => setShowForm(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Create Delegation
          </Button>
        )}

        <div className="space-y-2">
          {delegates?.map((delegation) => (
            <Card key={delegation.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <p className="font-medium">Delegate: {delegation.delegate_id}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(delegation.valid_from).toLocaleDateString()} -{" "}
                    {new Date(delegation.valid_until).toLocaleDateString()}
                  </p>
                  {delegation.delegation_reason && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {delegation.delegation_reason}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <Badge variant={delegation.is_active ? "outline" : "secondary"}>
                    {delegation.is_active ? "Active" : "Inactive"}
                  </Badge>
                  <Button variant="ghost" size="sm">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {(!delegates || delegates.length === 0) && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No active delegations
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
